/* eslint-disable no-unused-vars */
import React, { useReducer, useEffect, useRef, useMemo } from 'react';
import { Modal as AntModal } from 'antd';
import 'broadcastchannel-polyfill';
import RouterWebSDK from '@wkd/router-sdk/es/boot';

import { useSystemConfig, useRouterStatus } from '#/store';
import { debounce, getEnvIsGray, getQueryParams } from '#/util';
import { getPageConfigByPath } from '#/util/router';
import userInfo from '#/util/userInfo';
import { routerReducer, routerInitialState } from './context';
import AdsLayout from './layout/Ads';
import CreativeToolLayout from './layout/CreativeTool';

const cvaGrayOrigin = 'https://gray.phoenix-ads.com';
const cvaOrigin = 'https://www.phoenix-ads.com';

const cvaBetaGrayOrigin = 'https://gray.preview.phoenix-ads.com';
const cvaBetaOrigin = 'https://preview.phoenix-ads.com';

const RouterWeb = new RouterWebSDK();

const AdsMain = (props) => {
  const [systemConfig, updatePageConfig] = useSystemConfig();
  const [{ ActiveConfig: originActiveConfig }, updateContentNode, updateContentNodeList, updateActiveConfig] =
    useRouterStatus();
  const [state, dispatch] = useReducer(routerReducer, routerInitialState);
  // 使用 ref 进行索引，避免闭包问题
  const systemConfigRef = useRef();
  systemConfigRef.current = systemConfig;
  const switchAccountTipModalRef = useRef();
  const modalRef = useRef();
  const curPrimaryAccountNameRef = useRef('');
  const showDownMenuRef = useRef();
  showDownMenuRef.current = state.showDownMenu;

  const routerStateRef = useRef();
  routerStateRef.current = state;

  const { contentNode, contentNodeList, activeConfig } = RouterWeb.useRouterFederated(null);
  const Layout = useMemo(() => {
    if (systemConfig?.curTopPageConfig?.layout?.layout === 'creativeTool') {
      return CreativeToolLayout;
    }
    return AdsLayout;
  }, [systemConfig?.curPageConfig]);

  useEffect(() => {
    if (activeConfig) {
      const updateStatus = () => {
        const targetPageConfig = getPageConfigByPath(systemConfig.allPageConfig, activeConfig.fullPath);
        // targetPageConfig TODO: 注意观察这里
        updatePageConfig(targetPageConfig);
        updateContentNode(contentNode);
        updateContentNodeList(contentNodeList);
        updateActiveConfig(activeConfig);
      };
      // 同样的组件，没有更新
      if (originActiveConfig && activeConfig.tag === originActiveConfig.tag) {
        // do nothing
      } else {
        if (activeConfig.tryRefresh) {
          // 重新渲染

          const newContentList = contentNodeList.map((item) =>
            item.fullPath === activeConfig.fullPath ? null : item,
          );
          updateContentNodeList(newContentList);

          setTimeout(() => {
            updateStatus();
          }, 500);
          return;
        }
        updateStatus();
      }
    }
  }, [contentNode, activeConfig, contentNodeList]);

  /**
   * 解析 location.hash
   * @returns 解析 hash 之后的值
   */
  function parseSearchParams(searchParams) {
    const result = {};
    for (const [key, value] of searchParams.entries()) {
      result[key] = value;
    }
    return result;
  }

  function onPopState(event) {
    const { state: popState, pageTitle, target } = event;

    const routerQuery = {};

    if (target && target.location && target.location.search) {
      const searchParms = new URLSearchParams(target.location.search);
      Array.from(searchParms.keys()).forEach((item) => {
        routerQuery[item] = searchParms.get(item);
      });
    }

    window.router.go(window.location.pathname, {
      ...popState,
      tryRefresh: false,
      shouldPushState: false,
      pageTitle,
      routerQuery,
    });

    // 获取当前的hash值
    const currentHash = window.location.hash;
    // 解析当前的hash值
    const currentHashParams = new URLSearchParams(currentHash.slice(1));

    // 当触发了 popstate 事件后，主动发出 adsHashChange 事件，在子系统当中进行事件接收
    // 将 hash 当中存储的筛选参数通过事件进行传递
    const evt = new CustomEvent('adsHashChange', {
      detail: parseSearchParams(currentHashParams),
    });
    window.dispatchEvent(evt);
  }
  const showModal = (name) => {
    const Modal = window?.adsComponents?.Modal ?? AntModal;

    Modal.warning({
      icon: null,
      closable: false,
      title: 'Login expired',
      content: `The new main account【${name}】you selected accesses in WKD the advertiser platform. The login status of the current account has expired. Please refresh the browser before proceeding.`,
      okText: 'Refresh',
      onOk: () => {
        window.location.reload();
      },
      width: 480,
      style: { top: 160 },
    });
  };

  const handleDebounceMessageByBusinessLogin = debounce((event) => {
    // 确保消息来自灰度或线上的 WkdBusiness
    if (
      (getEnvIsGray() && (event.origin === cvaGrayOrigin || event.origin === cvaBetaGrayOrigin)) ||
      event.origin === cvaOrigin ||
      event.origin === cvaBetaOrigin
    ) {
      const { primaryAccountId, primaryAccountName, reloadWindow } = event.data || {};
      const _primaryAccountId = userInfo.getPrimaryAccountId();
      const isPreFlag = reloadWindow && primaryAccountId && primaryAccountName && _primaryAccountId;
      if (isPreFlag && +primaryAccountId !== +_primaryAccountId) {
        // 如果已经存在了穿越Modal，并且再次穿越进来的主账户是相同的，不进行任何处理
        if (
          primaryAccountName &&
          curPrimaryAccountNameRef.current === primaryAccountName &&
          modalRef.current
        ) {
          return;
        }
        // 如果已经存在了穿越Modal，但是
        if (curPrimaryAccountNameRef.current !== primaryAccountName && modalRef.current) {
          modalRef.current?.destroy();
        }
        modalRef.current = showModal(primaryAccountName);
        curPrimaryAccountNameRef.current = primaryAccountName;
      }

      // 如果存在了穿越的Modal，在中间经过了N次穿越之后，又穿越回了原账户，这时就不需要刷新浏览器了，需要关闭刷新浏览器的弹窗
      if (isPreFlag && +primaryAccountId === +_primaryAccountId && modalRef.current) {
        // 关闭弹窗，恢复成初始状态
        modalRef.current.destroy();
        curPrimaryAccountNameRef.current = '';
      }
    }
  }, 500);

  const handleMessage = (event) => {
    const Modal = window?.adsComponents?.Modal ?? AntModal;

    // 刷新页面
    if (event && event.data && event.data.type === 'reload') {
      const { windowId } = event.data;
      if (window.__WINDOW_ID__ !== windowId) {
        if (switchAccountTipModalRef.current) {
          return;
        }
        switchAccountTipModalRef.current = Modal.warning({
          icon: null,
          closable: false,
          title: 'Account Switch Notice',
          content: `To ensure the accuracy of your actions, please refresh the page before proceeding. Account switching is currently in progress.`,
          okText: 'Refresh',
          onOk: () => {
            window.location.reload();
          },
          width: 480,
        });
      }
    } else {
      handleDebounceMessageByBusinessLogin(event); // 或者使用 handleDebouncedMessage(message);
    }
  };
  useEffect(() => {
    // 页面信息获取后再进行基础能力的挂载
    if (systemConfig.allPageConfig.length) {
      const channel = new BroadcastChannel('messageChannel');
      channel.addEventListener('message', handleMessage);
      window.addEventListener('popstate', onPopState);
      window.__WINDOW_ID__ = +Date.now();

      const getDeepPageConfig = (allPageConfig) =>
        allPageConfig.reduce((acc, cur) => {
          const { subPage } = cur;
          if (subPage) {
            const allSubPage = Object.values(subPage);
            // eslint-disable-next-line no-param-reassign
            acc = acc.concat(getDeepPageConfig(allSubPage));
          }
          acc.push(cur);
          return acc;
        }, []);

      // 初始化所有路由\
      const pageConfigList = getDeepPageConfig(systemConfig.allPageConfig);

      RouterWeb.init(
        pageConfigList,
        pageConfigList.find((item) => item.pageId === 'Oops'),
        (title) => `${title} - Phoenix Ads`,
      );

      // 调用路径初始化 TODO: 初始化页面跳转

      // 初始化页面跳转
      window.router.go(`${window.location.pathname}`, {
        shouldPushState: false,
        // 转对象
        routerQuery: getQueryParams(window.location.search),
      });

      if (systemConfigRef.current.curPageConfig.name) {
        document.title = `${systemConfigRef.current.curPageConfig.name} - Phoenix Ads`;
      }

      return () => {
        window.removeEventListener('popstate', onPopState);
        channel.removeEventListener('message', handleMessage);
      };
    }
  }, [systemConfig.allPageConfig]);

  return <Layout systemConfig={systemConfig} state={state} dispatch={dispatch} {...props} />;
};

export default AdsMain;
